import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import "../../../../../App.css";
import "../../../../_Shared/utility.css";
import Card from "../../../../Card/Card";
import "./FindSurgeon.css";
import { USBanner } from "../../../../Banner/Banner";
import { Helmet } from "react-helmet-async";
import { cdn } from "../../../../../consts/cdn";
import { api } from "../../../../../consts/api";
import { GeoAltFill } from "react-bootstrap-icons";
import SkeletonDoctor from "../../../../../skeletons/FindASurgeon/SkeletonDoctor";
import { sendEventGTM } from "../../../../../utilities/GoogleAnalyticsEvents";
import { SearchBox } from "@mapbox/search-js-react";

//Banners
const BannerDesktop = cdn.images + "FindASurgeon/Banner-Desktop.jpg";
const BannerMobile = cdn.images + "FindASurgeon/Banner-Mobile.jpg";

const mapboxAccessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Mapbox Searchbox Styling
const searchBoxStyleString = `
  .Input:focus {  
  border: 1px solid rgba(0,0,0,.3);}
  @media (min-width: 1px) {
      .Input { width: 251px;}
    }
      @media (min-width: 450px) {
      .Input { width: 328px; }
    }
      @media (min-width: 600px) {
      .Input { width: 376px; }
    }
      @media (min-width: 1200px) {
      .Input { width: 400px; }
    }
      @media (min-width: 1500px) {
      .Input { width: 450px; }
    }  
    }`;

const searchBoxThemeVariables = {
  boxShadow: "none",
  border: "1px solid rgba(0,0,0,.3)",
};

function FindSurgeon() {
  const [allSurgeons, setAllSurgeons] = useState();
  const [surgeons, setSurgeons] = useState();
  const [noSearchResults, setNoSearchResults] = useState(false);
  const errorMsg =
    "There are no surgeons available. Please try a different location.";

  const [currentPage, setCurrentPage] = useState(0);
  const [pageBeforeSearch, setPageBeforeSearch] = useState(0);
  const surgeonsPerPage = 20;

  // Calculate paginated surgeons
  const pageCount = surgeons ? Math.ceil(surgeons.length / surgeonsPerPage) : 0;
  const offset = currentPage * surgeonsPerPage;
  const currentSurgeons = surgeons
    ? surgeons.slice(offset, offset + surgeonsPerPage)
    : [];

  async function getAllSurgeons() {
    try {
      const allSurgeons = await fetch(api + "/surgeon").then((response) =>
        response.json(),
      );

      setAllSurgeons(allSurgeons);
      setSurgeons(allSurgeons);
    } catch (error) {
      console.log(error);
    }
  }

  async function getNearbySurgeons(lat, lon) {
    try {
      const nearbySurgeons = await fetch(
        api + `/surgeon?latitude=${lat}&longitude=${lon}`,
      ).then((response) => response.json());

      setSurgeons(nearbySurgeons);
      setNoSearchResults(false);
      setCurrentPage(0);
    } catch (error) {
      setSurgeons([]);
      setNoSearchResults(true);
      setCurrentPage(0);
    }
  }

  async function getSurgeonsByState(state) {
    try {
      const stateSurgeons = await fetch(
        api + `/surgeon/GetStateSurgeons/${state}`,
      ).then((response) => response.json());

      setSurgeons(stateSurgeons);
      setNoSearchResults(false);
      setCurrentPage(0);
    } catch (error) {
      console.log(error);
      setSurgeons([]);
      setNoSearchResults(true);
      setCurrentPage(0);
    }
  }

  useEffect(() => {
    getAllSurgeons();
  }, []);

  // Mapbox Search Box Handlers
  function handleSuggestionSelect(suggestion) {
    if (!suggestion) {
      setSurgeons(allSurgeons);
      setNoSearchResults(false);
    } else {
      // only save the page the user was on before using search functionality
      if (surgeons === allSurgeons) {
        setPageBeforeSearch(currentPage);
      }
      // extract details from suggestion
      const [feature] = suggestion.features;
      const {
        properties: {
          feature_type,
          context: {
            region: { name: state, region_code },
            place: { name: city } = {},
            locality: { name: locality } = {},
          },
        },
        geometry: { coordinates },
      } = feature;

      if (feature_type === "region") {
        getSurgeonsByState(region_code);
      } else {
        const latitude = coordinates[1];
        const longitude = coordinates[0];

        let cityOrLocality = "";

        if (feature_type === "place" || feature_type === "postcode") {
          cityOrLocality = city;
        } else if (feature_type === "locality") {
          cityOrLocality = locality;
        }

        if (cityOrLocality) {
          const locationEventParams = {
            fas_city: cityOrLocality,
            fas_state: state,
            fas_location: `${cityOrLocality}, ${state}`,
          };

          sendEventGTM("FaS-search", locationEventParams);

          // to fire when the reddit pixel fas event is also sent
          sendEventGTM("fas-search_reddit", locationEventParams);
        }
        getNearbySurgeons(latitude, longitude);
      }
    }
  }

  function handleClearSearchBox() {
    setSurgeons(allSurgeons);
    setNoSearchResults(false);
    setCurrentPage(pageBeforeSearch);
  }

  function interceptSearch(input) {
    if (input.length < 3) {
      return null;
    } else {
      return input;
    }
  }

  // Handle page change
  function handlePageClick({ selected }) {
    setCurrentPage(selected);
    const topSearchDiv = document.getElementById("search-div");
    if (topSearchDiv) {
      const headerOffset = 80;
      const elementPosition =
        topSearchDiv.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - headerOffset,
        behavior: "instant",
      });
    }
  }

  return (
    <div className="findSurgeon">
      <Helmet>
        <title>vNOTES Hysterectomy</title>
        <meta
          name="description"
          content="vNOTES (vaginal natural orifice transluminal endoscopic surgery) is another minimally invasive approach to consider for your hysterectomy and other gynecologic…"
        />
      </Helmet>
      <USBanner
        imgdesktop={BannerDesktop}
        imgmobile={BannerMobile}
        text1="FIND A vNOTES"
        bold2="SURGEON"
        color="#365B7E"
      />
      <div className="content">
        <div className="findSurgeon__div-discretion">
          <p className="paragraph findSurgeon__paragraph">
            This resource is provided to assist you in finding a vNOTES surgeon
            and is for informational purposes only. It is not a complete listing
            of all surgeons who perform vNOTES.{" "}
            <a
              className="directory-disclaimer-link"
              href="#directory-disclaimer"
            >
              See full disclaimer below.
            </a>{" "}
            It's important to talk to your doctor to determine if vNOTES or
            another procedure is recommended to treat your specific condition.
          </p>
          <br />
          <p className="paragraph findSurgeon__paragraph">
            If you are a vNOTES surgeon and are interested in being included in
            this directory, please contact{" "}
            <a
              className="findSurgeon__anchor-email"
              href="mailto:vNOTES@appliedmedical.com"
            >
              vNOTES@appliedmedical.com
            </a>{" "}
            for more information.
          </p>
        </div>

        <div className="search-div" id="search-div">
          <div className="search-container">
            <span className="findSurgeon__span-icon-1">
              <GeoAltFill color="#faa21e" className="map-pin-icon" />
            </span>

            <SearchBox
              accessToken={mapboxAccessToken}
              placeholder="Enter a ZIP Code, City or State"
              options={{
                language: "en",
                country: "US",
                limit: 10,
                types: ["place", "postcode", "locality", "region"],
              }}
              onRetrieve={handleSuggestionSelect}
              onClear={handleClearSearchBox}
              theme={{
                variables: searchBoxThemeVariables,
                cssText: searchBoxStyleString,
              }}
              interceptSearch={interceptSearch}
            />
          </div>
          <p className="error">{noSearchResults ? errorMsg : null}</p>
        </div>

        <div className="findSurgeon__div-row">
          {!surgeons
            ? // Loading state
              Array.from(Array(surgeonsPerPage).keys()).map((n) => (
                <SkeletonDoctor key={n}></SkeletonDoctor>
              ))
            : noSearchResults
              ? null
              : // Show surgeons
                currentSurgeons.map((surgeon) => (
                  <Card
                    key={surgeon.id}
                    img={cdn.surgeonImages + surgeon.profileImage}
                    firstName={surgeon.firstName}
                    middleInitial={surgeon.middleInitial}
                    lastName={surgeon.lastName}
                    title={surgeon.title}
                    specialty={surgeon.specialty}
                    location={surgeon.hospital}
                    location2={surgeon.location}
                    address1={surgeon.address}
                    additional={surgeon.additionalInformation}
                    city={surgeon.city}
                    state={surgeon.state}
                    zip={surgeon.zipCode}
                    phone={surgeon.phone}
                    href={surgeon.websiteUrl}
                    distance={surgeon.distance}
                  />
                ))}
        </div>

        {surgeons && surgeons.length > 0 && (
          <ReactPaginate
            previousLabel={
              currentPage === 0 ? null : (
                <div className="pagination-arrow-label">
                  <svg width="20" height="20" viewBox="0 0 20 20">
                    <path
                      d="M13 15l-5-5 5-5"
                      stroke="#365B7E"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Previous</span>
                </div>
              )
            }
            nextLabel={
              currentPage === pageCount - 1 ? null : (
                <div className="pagination-arrow-label">
                  <span>Next</span>
                  <svg width="20" height="20" viewBox="0 0 20 20">
                    <path
                      d="M7 5l5 5-5 5"
                      stroke="#365B7E"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )
            }
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            forcePage={currentPage}
            containerClassName={"pagination"}
            activeClassName={"active-page"}
          />
        )}
        <div
          className="directory-disclaimer-container"
          id="directory-disclaimer"
        >
          <p className="directory-disclaimer-header">About this directory</p>
          <p className="directory-disclaimer-text">
            The surgeons listed in this tool have voluntarily self-reported and
            attested that they perform the vNOTES procedure. Applied Medical
            does not verify, and makes no representations or warranties
            regarding, the accuracy or completeness of the information provided
            by the surgeons. The inclusion of a surgeon in this directory does
            not constitute an endorsement or recommendation by Applied Medical.
          </p>
        </div>
      </div>
    </div>
  );
}

export default FindSurgeon;
